import { useState } from "react";
import { api } from "../lib/api";
import { PageHeader } from "../components/Layout";
import { useAuth } from "../context/AuthContext";
import { Check, Flower2, Sprout, Home, Gem, Loader2 } from "lucide-react";
import { toast } from "sonner";

const TIERS = [
  {
    key: "seed", name: "Seed", icon: Sprout, price: "Free", tagline: "Start growing, forever free",
    features: ["One active intention", "One pod", "Moments & community", "Gentle step reminders"],
  },
  {
    key: "bloom", name: "Bloom", icon: Flower2, price: null, tagline: "For the season you're in",
    features: ["Unlimited intentions & next steps", "As many pods as you tend", "Share-as-image cards", "Reflection history", "Rituals when they arrive"],
  },
  {
    key: "host", name: "Host", icon: Home, price: "$29/mo", lookup: "host_monthly", tagline: "Keep your whole circle blooming",
    features: ["Run a community", "Member management", "Community feed & gatherings", "Invite tools", "Gentle admin metrics"],
  },
  {
    key: "founding", name: "Founding Circle", icon: Gem, price: "$150 once", lookup: "founding_circle", tagline: "First 100 members, forever",
    features: ["Lifetime Bloom", "Founding member badge", "Your name in our story"],
  },
];

export default function Pricing() {
  const { user } = useAuth();
  const [yearly, setYearly] = useState(false);
  const [busy, setBusy] = useState("");

  const checkout = async (lookupKey) => {
    setBusy(lookupKey);
    try {
      const res = await api.post("/payments/checkout", { lookup_key: lookupKey, origin_url: window.location.origin });
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start checkout");
      setBusy("");
    }
  };

  const bloomLookup = yearly ? "bloom_yearly" : "bloom_monthly";

  return (
    <div data-testid="pricing-page" className="space-y-8">
      <PageHeader eyebrow="Membership" title="Grow at your own pace"
        description="Bouquet stays free for the people invited into pods — always. These plans are for the ones tending their own growth, or keeping a whole circle blooming." />

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {TIERS.map((t) => {
          const Icon = t.icon;
          const isCurrent = t.key === "seed" ? !user?.tier || user.tier === "Founding Member" : user?.tier === t.name;
          const lookup = t.key === "bloom" ? bloomLookup : t.lookup;
          return (
            <div key={t.key} data-testid={`tier-card-${t.key}`}
              className={`bg-card border rounded-2xl p-6 flex flex-col gap-4 ${t.key === "bloom" ? "border-primary shadow-sm" : "border-border"}`}>
              <div className="flex items-center justify-between">
                <Icon className="h-5 w-5 text-primary" />
                {isCurrent && <span className="text-[10px] font-semibold uppercase tracking-wider text-primary" data-testid={`tier-current-${t.key}`}>Your plan</span>}
              </div>
              <div>
                <h3 className="font-display text-xl font-medium">{t.name}</h3>
                <p className="text-xs text-muted-foreground mt-1">{t.tagline}</p>
              </div>
              {t.key === "bloom" ? (
                <div className="space-y-2">
                  <div className="flex rounded-full border border-border p-0.5 text-xs font-semibold" data-testid="bloom-billing-toggle">
                    <button data-testid="bloom-monthly-toggle" onClick={() => setYearly(false)}
                      className={`flex-1 py-1.5 rounded-full transition-colors ${!yearly ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>Monthly</button>
                    <button data-testid="bloom-yearly-toggle" onClick={() => setYearly(true)}
                      className={`flex-1 py-1.5 rounded-full transition-colors ${yearly ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>Yearly</button>
                  </div>
                  <p className="font-display text-2xl font-medium">{yearly ? "$72/yr" : "$8/mo"}</p>
                </div>
              ) : (
                <p className="font-display text-2xl font-medium">{t.price}</p>
              )}
              <ul className="space-y-2 flex-1">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-xs text-muted-foreground">
                    <Check className="h-3.5 w-3.5 text-primary shrink-0 mt-0.5" /> {f}
                  </li>
                ))}
              </ul>
              {lookup && (
                <button data-testid={`checkout-${lookup}`} onClick={() => checkout(lookup)} disabled={!!busy || isCurrent}
                  className="w-full bg-primary text-primary-foreground py-2.5 rounded-full text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2">
                  {busy === lookup && <Loader2 className="h-4 w-4 animate-spin" />}
                  {isCurrent ? "Current plan" : t.key === "founding" ? "Claim a spot" : `Choose ${t.name}`}
                </button>
              )}
            </div>
          );
        })}
      </div>

      <p className="text-xs text-muted-foreground max-w-xl" data-testid="pricing-note">
        Payments are handled securely by Stripe. Cancel anytime — no guilt trips, no dark patterns. Your intentions, pods, and moments stay yours either way.
      </p>
    </div>
  );
}
